import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Checkbox, TextField, IconButton, Tooltip, Button, LinearProgress } from '@mui/material';
import { Add, Delete, Edit, Check, Close, PlaylistAdd } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useHasPermission } from '../utils/permissionUtils';
import { addChecklistItem, renameChecklistItem, removeChecklistItem, toggleChecklistItem, importTaskChecklist } from '../redux/ducks/task';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { showAlert } from '../redux/slices/alerts';
import ChecklistTemplatePickerDialog from './ChecklistTemplatePickerDialog';

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const HelperText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
}));

const ProgressRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const ItemRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0),
    '&:hover .checklist-item-actions': {
        opacity: 1,
    },
}));

const ItemName = styled(Typography, { shouldForwardProp: (prop) => prop !== 'done' })(({ theme, done }) => ({
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
    textDecoration: done ? 'line-through' : 'none',
    color: done ? theme.palette.text.disabled : theme.palette.text.primary,
}));

const ItemActions = styled(Box)(() => ({
    display: 'flex',
    opacity: 0,
    transition: 'opacity 120ms ease',
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const AddRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const sortItems = (task) => {
    const list = Array.isArray(task?.checklist?.items) ? [...task.checklist.items] : [];
    return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const TaskChecklistSection = ({ task, onTaskUpdated, isLocked = false }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const currentUserId = useSelector((state) => state.auth.userId);
    const taskId = task?._id;

    const canEditChecklist = hasPermission('editProjectTask') && !isLocked;
    const isResponsible = !!task?.responsible?._id && !!currentUserId && String(task.responsible._id) === String(currentUserId);
    const canCheckOff = (hasPermission('editProjectTask') || isResponsible) && !isLocked;

    const [items, setItems] = useState(() => sortItems(task));
    const [newItemName, setNewItemName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [adding, setAdding] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [importing, setImporting] = useState(false);

    useEffect(() => {
        setItems(sortItems(task));
    }, [task?._id, task?.checklist?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const completedCount = useMemo(() => items.filter((i) => i.completed).length, [items]);

    const propagate = useCallback((updatedTask) => {
        if (!updatedTask) return;
        if (typeof onTaskUpdated === 'function') onTaskUpdated(updatedTask);
        if (updatedTask.status && updatedTask.status !== task?.status) {
            dispatch(triggerRefresh({ triggerName: 'project' }));
        }
    }, [dispatch, onTaskUpdated, task?.status]);

    const handleToggle = useCallback((item) => {
        if (!canCheckOff) return;
        const nextCompleted = !item.completed;
        const previous = items;
        setItems((prev) => prev.map((it) => (it._id === item._id ? { ...it, completed: nextCompleted } : it)));
        dispatch(toggleChecklistItem(taskId, item._id, nextCompleted, (data, error) => {
            if (error || !data?.task) {
                setItems(previous);
                return;
            }
            propagate(data.task);
        }));
    }, [canCheckOff, items, dispatch, taskId, propagate]);

    const handleAdd = useCallback(() => {
        const name = newItemName.trim();
        if (!name || !canEditChecklist || adding) return;
        setAdding(true);
        dispatch(addChecklistItem(taskId, { name }, (data, error) => {
            setAdding(false);
            if (error || !data?.task) return;
            setNewItemName('');
            propagate(data.task);
        }));
    }, [newItemName, canEditChecklist, adding, dispatch, taskId, propagate]);

    const handleImportTemplate = useCallback((checklistTemplateId) => {
        if (!canEditChecklist || importing) return;
        setImporting(true);
        dispatch(importTaskChecklist(taskId, checklistTemplateId, (data, error) => {
            setImporting(false);
            if (error || !data?.task) return;
            setPickerOpen(false);
            propagate(data.task);
        }));
    }, [canEditChecklist, importing, dispatch, taskId, propagate]);

    const handleStartEdit = useCallback((item) => {
        setEditingId(item._id);
        setEditingName(item.name);
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingId(null);
        setEditingName('');
    }, []);

    const handleSaveEdit = useCallback((item) => {
        const name = editingName.trim();
        if (!name) {
            dispatch(showAlert({ severity: 'warning', message: 'Checklist item name cannot be empty' }));
            return;
        }
        if (name === item.name) {
            handleCancelEdit();
            return;
        }
        const previous = items;
        setItems((prev) => prev.map((it) => (it._id === item._id ? { ...it, name } : it)));
        setEditingId(null);
        dispatch(renameChecklistItem(taskId, item._id, name, (data, error) => {
            if (error || !data?.task) {
                setItems(previous);
                return;
            }
            propagate(data.task);
        }));
    }, [editingName, items, dispatch, taskId, propagate, handleCancelEdit]);

    const handleRemove = useCallback((item) => {
        if (!canEditChecklist) return;
        const previous = items;
        setItems((prev) => prev.filter((it) => it._id !== item._id));
        dispatch(removeChecklistItem(taskId, item._id, (data, error) => {
            if (error || !data?.task) {
                setItems(previous);
                return;
            }
            propagate(data.task);
        }));
    }, [canEditChecklist, items, dispatch, taskId, propagate]);

    if (items.length === 0 && !canEditChecklist) {
        return null;
    }

    return (
        <Box>
            <HeaderRow>
                <SectionTitle>Checklist</SectionTitle>
                <HeaderActions>
                    {items.length > 0 && (
                        <Typography variant="caption" color="textSecondary">
                            {completedCount}/{items.length} done
                        </Typography>
                    )}
                    {canEditChecklist && (
                        <Tooltip title="Add the steps from a saved checklist template to the end of this list">
                            <Button size="small" startIcon={<PlaylistAdd />} onClick={() => setPickerOpen(true)}>
                                Import from template
                            </Button>
                        </Tooltip>
                    )}
                </HeaderActions>
            </HeaderRow>
            <HelperText variant="caption">
                Steps to tick off while working this task. Completing the last item moves the task to Ready for Review.
            </HelperText>

            {items.length > 0 && (
                <ProgressRow>
                    <Box flex={1}>
                        <LinearProgress variant="determinate" value={items.length ? (completedCount / items.length) * 100 : 0} />
                    </Box>
                </ProgressRow>
            )}

            {items.map((item) => {
                const isItemEditing = editingId === item._id;
                return (
                    <ItemRow key={item._id}>
                        <Tooltip title={canCheckOff ? '' : 'Only the assigned team can check off items on this task.'} disableHoverListener={canCheckOff}>
                            <span>
                                <Checkbox
                                    size="small"
                                    checked={!!item.completed}
                                    disabled={!canCheckOff}
                                    onChange={() => handleToggle(item)}
                                />
                            </span>
                        </Tooltip>
                        {isItemEditing ? (
                            <>
                                <TextField
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveEdit(item);
                                        if (e.key === 'Escape') handleCancelEdit();
                                    }}
                                    variant="standard"
                                    fullWidth
                                    autoFocus
                                />
                                <Tooltip title="Save">
                                    <IconButton size="small" onClick={() => handleSaveEdit(item)} aria-label="save checklist item">
                                        <Check fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                    <IconButton size="small" onClick={handleCancelEdit} aria-label="cancel edit">
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                <ItemName variant="body2" done={!!item.completed}>
                                    {item.name}
                                </ItemName>
                                {canEditChecklist && (
                                    <ItemActions className="checklist-item-actions">
                                        <Tooltip title="Rename">
                                            <IconButton size="small" onClick={() => handleStartEdit(item)} aria-label="rename checklist item">
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove">
                                            <IconButton size="small" onClick={() => handleRemove(item)} aria-label="remove checklist item">
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </ItemActions>
                                )}
                            </>
                        )}
                    </ItemRow>
                );
            })}

            {canEditChecklist && (
                <AddRow>
                    <TextField
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAdd();
                        }}
                        placeholder="Add a step"
                        variant="standard"
                        fullWidth
                    />
                    <Button size="small" startIcon={<Add />} onClick={handleAdd} disabled={!newItemName.trim() || adding}>
                        Add
                    </Button>
                </AddRow>
            )}

            {canEditChecklist && (
                <ChecklistTemplatePickerDialog
                    open={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                    onImport={handleImportTemplate}
                    importing={importing}
                    existingItemCount={items.length}
                />
            )}
        </Box>
    );
};

export default TaskChecklistSection;
